import { PanelTitle } from 'components/layout/Panel'
import Panel from 'components/layout/Panel'
import { supportedCertifications } from 'types/profile'
import { useSelector } from 'react-redux'
import { Container, Text } from 'roadie-ui'
import { optimizely } from 'Optimizely'
import ButtonLink from 'components/Button/ButtonLink'
import { LocalizedText, useLocalization } from 'localization'
import type { ProfileDeliveryCertification } from 'data/driverCertifications'

const CertificationToGetDisplay: React.FC<{
  length: number
  index: number
  image_url?: string
  name: string
  description?: string
  cert_link: string
  is_locked: boolean
}> = (props) => {
  return (
    <Container.Grid
      key={props.index}
      $gridTemplateColumns="4.9rem auto"
      $padding="3rem"
      $gridGap="3rem"
      $borderColor="neutral-light"
      $borderStyle="solid"
      $borderWidth={props.index !== props.length - 1 ? '0 0 1px 0' : '0'}
    >
      <img src={props.image_url} alt={props.name} style={{ opacity: 0.3 }} />
      <Container.Grid $gridGap="0.5rem">
        <Text.H3 $fontWeight="500" $fontSize="2.4rem" $color="neutral-black" $textAlign="left">
          {props.name}
        </Text.H3>
        <Text.Body2 $textAlign="left">{props.description}</Text.Body2>
        {!props.is_locked && (
          <ButtonLink variant="tertiary" type="button" $padding="1rem 0 0 0" to={props.cert_link}>
            <LocalizedText translationKey="dweb_dashboard_get_more_gigs_get_certified" />
          </ButtonLink>
        )}
      </Container.Grid>
    </Container.Grid>
  )
}

const GetMoreGigs: React.FC = () => {
  const isLocked = useSelector<any, boolean>((state) => state.isLocked)
  const { t } = useLocalization()
  const roadieXDFF = optimizely.decide('drive_roadiexd_module_available')

  const trainingCertifications = useSelector<any, ProfileDeliveryCertification[]>(
    (state) => state.trainingCertifications?.delivery_certifications_drivers ?? []
  )
  const activeTrainingCertificationNames = new Set(
    trainingCertifications.filter((tc) => !tc.is_revoked).map((tc) => tc.name)
  )
  const revokedTrainingCertificationNames = new Set(
    trainingCertifications.filter((tc) => tc.is_revoked).map((tc) => tc.name)
  )

  const certificationsToShow = Object.entries(supportedCertifications)
    .filter(([, certification]) => certification.available_for_drivers) // filter to certs available for drivers
    .filter(([certName]) => !activeTrainingCertificationNames.has(certName)) // filter to certs driver doesn't have
    .filter(([certName]) => !revokedTrainingCertificationNames.has(certName)) // filter out certs revoked from driver
    .filter(
      ([, certification]) =>
        !certification.eligibility_cert || activeTrainingCertificationNames.has(certification.eligibility_cert)
    ) // only display if they have corresponding eligiblilty cert
    .filter(([certName]) => (certName === 'roadie_xd' ? roadieXDFF.enabled : true)) // only show roadie_xd if the FF is on
    .map(([certName, certification]) => ({
      name: certName,
      display_name: certification.display_name + ' Gigs',
      description: certification.learning_description_key ? t(certification.learning_description_key) : undefined,
      image_url: certification.url,
      cert_link: certification.cert_link || '', // there should always be a cert link if the certification is available for drivers
    }))

  return (
    <>
      <PanelTitle>
        <LocalizedText translationKey="dweb_dashboard_get_more_gigs" />
      </PanelTitle>
      <Panel noPadding>
        <Container.Grid>
          {certificationsToShow.length ? (
            certificationsToShow.map((certification, index) => (
              <CertificationToGetDisplay
                key={index}
                length={certificationsToShow.length}
                index={index}
                image_url={certification.image_url}
                name={certification.display_name}
                description={certification.description}
                cert_link={certification.cert_link}
                is_locked={isLocked}
              />
            ))
          ) : (
            <Container.GridItem $padding="2.4rem">
              <Text.Body1 $fontWeight="00" $fontSize="1.6rem">
                <LocalizedText translationKey="dweb_dashboard_get_more_gigs_youve_earned_all_eligible_certifications_new_ones_will" />
              </Text.Body1>
            </Container.GridItem>
          )}
        </Container.Grid>
      </Panel>
    </>
  )
}

export default GetMoreGigs
